import { useEffect, useState } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { DataTable } from "@/components/common/DataTable";
import { Loader } from "@/components/common/Loader";
import { shortDate } from "@/lib/format";
import api from "@/lib/api";
import { Receipt } from "lucide-react";

const USD = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const RED = "#B10DC9";

export default function UsTax() {
  const [rates, setRates] = useState(null);
  const [reg, setReg] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/us-tax/rates"), api.get("/us-tax/register")]).then(([r, g]) => { setRates(r.data); setReg(g.data); });
  }, []);
  if (!rates || !reg) return <Loader label="Loading US sales & use tax…" />;
  const sm = reg.summary;

  const cols = [
    { key: "order_no", label: "Order", render: (r) => <span className="font-mono text-xs text-[#B10DC9]">{r.order_no}</span> },
    { key: "customer_name", label: "Customer", render: (r) => (<div><div>{r.customer_name}</div><div className="font-mono text-[10px] text-[#6a6d70]">{r.customer_code}</div></div>) },
    { key: "date", label: "Date", render: (r) => shortDate(r.date) },
    { key: "base", label: "Net Sale", render: (r) => <span className="font-mono">{USD(r.base)}</span> },
    { key: "tax_rate", label: "Rate", render: (r) => (r.exempt ? <span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">Exempt · {r.exemption}</span> : <span className="font-mono">{r.tax_rate}%</span>) },
    { key: "tax", label: "Tax", render: (r) => <span className="font-mono">{USD(r.tax)}</span> },
    { key: "total", label: "Total", render: (r) => <span className="font-mono font-semibold">{USD(r.total)}</span> },
  ];

  return (
    <div data-testid="us-tax-page">
      <PageHeader title="US Sales & Use Tax — Las Vegas" subtitle="Nevada destination-based sales & use tax: jurisdiction rates, taxable vs resale-exempt register, and monthly filing (NV DoT TXR-01.01)." icon={Receipt} accent={RED} />

      <div className="mb-6 grid gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-[#e5e9f0] bg-white p-5 shadow-sm md:col-span-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-[#32363a]">Rate Breakdown — {rates.state}</h3>
          <div className="mt-3 space-y-2">
            {rates.jurisdictions.map((j) => (
              <div key={j.name} className="flex justify-between border-b border-[#f0f2f5] pb-1.5 text-sm"><span>{j.name}</span><span className="font-mono">{j.rate}%</span></div>
            ))}
            <div className="flex justify-between pt-1 text-sm font-semibold"><span>Combined rate</span><span className="font-mono text-[#B10DC9]">{rates.combined_rate}%</span></div>
          </div>
          <div className="mt-3 rounded-lg bg-[#f7f8fa] px-3 py-2 text-xs text-[#6a6d70]">{rates.basis} · {rates.filing}<div className="mt-1">{rates.note}</div></div>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-1">
          <KpiCard testId="us-kpi-sales" label="Gross Sales" value={USD(sm.gross_sales)} accent={RED} />
          <KpiCard testId="us-kpi-tax" label="Tax Due (period)" value={USD(sm.tax_due)} sub={`${sm.orders} orders`} accent="#EF4444" />
        </div>
      </div>

      <div className="mb-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="us-kpi-taxable" label="Taxable Sales" value={USD(sm.taxable)} accent="#3B82F6" />
        <KpiCard testId="us-kpi-exempt" label="Exempt (Resale)" value={USD(sm.exempt)} accent="#10B981" />
        <KpiCard testId="us-kpi-rate" label="Combined Rate" value={`${reg.combined_rate}%`} accent={RED} />
        <KpiCard testId="us-kpi-orders" label="Transactions" value={sm.orders} accent="#6366F1" />
      </div>

      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">Sales & Use Tax Register — Las Vegas</h3>
      {reg.rows.length === 0 ? <div className="rounded-lg border border-[#e5e9f0] bg-white p-8 text-center text-sm text-[#6a6d70]">No Las Vegas sales transactions in this period.</div> : <DataTable testId="us-tax-register-table" columns={cols} rows={reg.rows} />}
    </div>
  );
}
